<template>
  <div>
    <div class="d-flex justify-content-between">
      <div class="d-flex excel-reports">
        <label>{{$t('labels.common.excel_reports')}}</label>
        <multiselect v-model="excel_report" :max-height="200"
                     placeholder="Choose report" label="value" track-by="value"
                     :options="paymentAndInvoicesReports" :multiple="false"
                     @select="loadExcelReport"
                     :close-on-select="true"></multiselect>
      </div>
      <div class="button-group">
        <img src="@/assets/images/common-icons/icon_clear.png" @click="cleanFilters"
             :title="$t('buttons.common.clear_filters')">
        <img src="@/assets/images/common-icons/icon_search.svg" @click="search"
             :title="$t('buttons.common.search')">
      </div>
    </div>
    <div class="search-form">
      <div class="search-form__filters">
        <div>
          <b-row>
            <b-col>
              <label>{{$t('labels.invoices.invoice_number')}}</label>
              <input class="form-control" v-model="searchData.invoice_number" @keyup.enter="search"
                     placeholder="Enter invoice No">
            </b-col>
            <b-col>
              <label>{{$t('labels.payments.payment_number')}}</label>
              <input class="form-control" v-model="searchData.payment_number" @keyup.enter="search"
                     placeholder="Enter payment No">
            </b-col>
            <b-col>
              <label>{{$t('labels.common.seller')}}</label>
              <multiselect v-model="searchData.seller" :max-height="200"
                           placeholder="Choose seller" label="name" track-by="name"
                           :options="partners" :multiple="true"
                           :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.invoices.invoice_date')}}</label>
              <datepicker v-model="searchData.invoice_date" range lang="en"
                          range-separator="/" :first-day-of-week="1"></datepicker>
            </b-col>
            <b-col>
              <label>{{$t('labels.payments.payment_date')}}</label>
              <datepicker v-model="searchData.payment_date" range lang="en"
                          range-separator="/" :first-day-of-week="1"></datepicker>
            </b-col>
            <b-col>
              <label>{{$t('labels.common.buyer')}}</label>
              <multiselect v-model="searchData.buyer" :max-height="200"
                           placeholder="Choose buyer" label="name" track-by="name"
                           :options="partners" :multiple="true"
                           :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>{{$t('labels.invoices.invoice_type')}}</label>
              <multiselect v-model="searchData.invoice_type" :max-height="200"
                           placeholder="Choose type" label="value" track-by="value"
                           :options="shortInvoiceTypesList" :multiple="true"
                           :close-on-select="false"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.invoices.official_sum_payment_status')}}</label>
              <multiselect v-model="searchData.paid_status_official" :max-height="200"
                           :multiple="true" :close-on-select="false"
                           placeholder="Choose offic. sum payment status" label="name" track-by="key"
                           :options="paymentStatuses"></multiselect>
            </b-col>
            <b-col>
              <label>{{$t('labels.payments.payment_form')}}</label>
              <multiselect v-model="searchData.payment_mode" :max-height="200"
                           placeholder="Choose payment form" label="name" track-by="name"
                           :options="paymentForms" :multiple="true"
                           :close-on-select="false"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col offset="4" cols="4">
              <label>{{$t('labels.invoices.additional_sum_payment_status')}}</label>
              <multiselect v-model="searchData.paid_status_additional" :max-height="200"
                           :multiple="true" :close-on-select="false"
                           placeholder="Choose addit. sum payment status" label="name" track-by="key"
                           :options="paymentStatuses"></multiselect>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <ag-grid-vue class="ag-theme-balham grid" :style="gridHeight" :gridOptions="paymentAndInvoicesGridOptions"
                           :columnDefs="paymentAndInvoicesColumns" :rowData="paymentAndInvoices"
                           :postProcessPopup="postProcessPopup">
              </ag-grid-vue>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { AgGridVue } from 'ag-grid-vue';
  import {
    mapGetters,
    mapState
  } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import {
    cleanFilters,
    saveFilters,
    setFilters,
    getArrayByProperty,
    checkSavedFilterData,
    getFileNameFromContentDisposition,
    transformDateFromDateRangeToStringFormatByIndex
  } from '@/helpers/commonHelpers';
  import { saveAs } from 'file-saver';
  import { COMMISSION, SERVICE } from '@/constants/invoiceTypes';
  import { gridMixins } from '@/mixins';

  export default {
    mixins: [gridMixins],
    components: {
      Multiselect,
      Datepicker,
      'ag-grid-vue': AgGridVue
    },
    data() {
      return {
        excel_report: '',
        paymentStatuses: [
          {name: 'Not paid', key: 'notpaid'},
          {name: 'Partly paid', key: 'notfullpaid'},
          {name: 'Paid', key: 'paid'}],
        paymentForms: [{name: 'Official', key: 'official'}, {name: 'Additional', key: 'additional'}],
        searchData: {
          invoice_date: '',
          payment_date: '',
          buyer: [],
          paid_status: [],
          seller: [],
          impact: [],
          invoice_type: [],
          invoice_number: '',
          payment_number: '',
          payment_mode: [],
          paid_status_official: [],
          paid_status_additional: []
        }
      }
    },
    methods: {
      getSearchParams() {
        return {
          invoice_number: this.searchData.invoice_number,
          invoice_date_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 0),
          invoice_date_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.invoice_date, 1),
          invoice_type: getArrayByProperty(this.searchData.invoice_type, 'key').toString(),
          paid_status: getArrayByProperty(this.searchData.paid_status, 'key').toString(),
          sellers: getArrayByProperty(this.searchData.seller, 'id').toString(),
          buyers: getArrayByProperty(this.searchData.buyer, 'id').toString(),
          payment_number: this.searchData.payment_number,
          payment_mode: getArrayByProperty(this.searchData.payment_mode, 'key').toString(),
          payment_date_after: transformDateFromDateRangeToStringFormatByIndex(this.searchData.payment_date, 0),
          payment_date_before: transformDateFromDateRangeToStringFormatByIndex(this.searchData.payment_date, 1),
          paid_status_official: getArrayByProperty(this.searchData.paid_status_official, 'key').toString(),
          paid_status_additional: getArrayByProperty(this.searchData.paid_status_additional, 'key').toString()
        }
      },
      search() {
        const searchData = this.getSearchParams();
        this.$store.dispatch('accounting/loadPaymentsAndInvoicesList', searchData);
        saveFilters('paymentsAndInvoicesList', this.searchData);
      },
      loadExcelReport(report) {
        const params = this.getSearchParams();
        this.$http.get(`accounting/generate_invoice_payment_report/${report.key}/`, {
          responseType: 'blob', params: params
        }).then((response, headers) => {
          const disposition = response.headers.get('content-disposition'),
            fileName = getFileNameFromContentDisposition(disposition, report.value);
          saveAs(response.body, fileName);
        })
      },
      cleanFilters() {
        cleanFilters(this.searchData);
      },
      clearData() {
        this.$store.commit('accounting/setPaymentsAndInvoicesList', []);
      }
    },
    beforeMount() {
      this.$store.cache.dispatch('woodware/loadShortInvoiceTypesList', {
        exclude: `${SERVICE},${COMMISSION}`,
        object_view: true
      });
      this.$store.dispatch('accounting/loadPaymentAndInvoicesReports');
      this.$store.dispatch('woodware/getPartners', {is_active: true});
      if (checkSavedFilterData('paymentsAndInvoicesList')) {
        this.searchData = setFilters('paymentsAndInvoicesList');
        this.search();
      }
    },
    beforeRouteEnter(to, from, next) {
      next(vm => {
        vm.clearData();
      });
    },
    computed: {
      ...mapGetters('accounting', {
        paymentAndInvoicesGridOptions: 'paymentAndInvoicesGridOptions'
      }),
      ...mapState('accounting', ['paymentAndInvoicesColumns', 'paymentAndInvoices', 'paymentAndInvoicesReports']),
      ...mapState('woodware', ['shortInvoiceTypesList', 'partners']),
      gridHeight() {
        return {height: 'calc(100vh - 360px)'}
      }
    }
  }
</script>

<style scoped lang="scss">
  .excel-reports {
    min-width: 200px;
    width: 30%;
  }

  .grid {
    height: 500px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/modules/sales/payments-and-invoices/PaymentsAndInvoicesList.vue